import type {
  Filter,
  FilterDefinition,
  FilterMatchMode,
} from "@/shared/components/ResourceView";
import {
  eventLabel,
  isSchedule,
  SUBSCRIBABLE_EVENT_TYPES,
  TARGET_TYPE_LABELS,
  TriggerTargetType,
  type TriggerV2Record,
} from "@/shared/domain/triggers";

/** Which firing source the list shows; the quick toggle beside the filters. */
export type FiresOnFilter = "all" | "events" | "schedule";

/** Filterable fields of the triggers list; evaluated in memory, see {@link triggerMatchesFilters}. */
export const TRIGGER_FILTER_DEFINITIONS: FilterDefinition[] = [
  {
    type: "string",
    field: "name",
    name: "Name",
    allowedComparators: ["CONTAINS", "EQUALS"],
  },
  {
    type: "set",
    field: "events",
    name: "Platform event",
    options: SUBSCRIBABLE_EVENT_TYPES.map((eventType) => ({
      value: eventType,
      label: eventLabel(eventType),
    })),
  },
  {
    type: "set",
    field: "targets",
    name: "Target type",
    options: Object.values(TriggerTargetType).map((type) => ({
      value: type,
      label: TARGET_TYPE_LABELS[type],
    })),
  },
  { type: "boolean", field: "enabled", name: "Enabled" },
  {
    type: "string",
    field: "modified_by",
    name: "Modified by",
    allowedComparators: ["CONTAINS", "EQUALS"],
  },
];

function isNegative(comparator: string): boolean {
  return comparator.startsWith("NOT_");
}

function stringMatches(
  haystack: string,
  comparator: string,
  needle: string,
): boolean {
  const h = haystack.toLowerCase();
  const n = needle.toLowerCase();
  switch (comparator) {
    case "EQUALS":
      return h === n;
    case "NOT_EQUALS":
      return h !== n;
    case "CONTAINS":
    case "LIKE":
      return h.includes(n);
    case "NOT_CONTAINS":
    case "NOT_LIKE":
      return !h.includes(n);
    default:
      return true;
  }
}

function matchesStringFilter(
  haystack: string,
  comparator: string,
  values: readonly string[],
): boolean {
  const needles = values.filter((value) => value.trim().length > 0);
  if (needles.length === 0) {
    return true;
  }
  return isNegative(comparator)
    ? needles.every((needle) => stringMatches(haystack, comparator, needle))
    : needles.some((needle) => stringMatches(haystack, comparator, needle));
}

function matchesMembership(
  owned: readonly string[],
  comparator: string,
  values: readonly string[],
): boolean {
  if (values.length === 0) {
    return true;
  }
  const anyOwned = values.some((value) => owned.includes(value));
  return isNegative(comparator) ? !anyOwned : anyOwned;
}

function matchesFilter(trigger: TriggerV2Record, filter: Filter): boolean {
  switch (filter.field) {
    case "name":
      return (
        filter.type === "string" &&
        matchesStringFilter(trigger.name, filter.comparator, filter.values)
      );
    case "modified_by":
      return (
        filter.type === "string" &&
        matchesStringFilter(
          trigger.modified_by,
          filter.comparator,
          filter.values,
        )
      );
    case "events":
      return (
        filter.type === "set" &&
        matchesMembership(
          isSchedule(trigger.fires_on) ? [] : trigger.fires_on.events,
          filter.comparator,
          filter.values.map((option) => option.value),
        )
      );
    case "targets":
      return (
        filter.type === "set" &&
        matchesMembership(
          trigger.targets.map((target) => target.type),
          filter.comparator,
          filter.values.map((option) => option.value),
        )
      );
    case "enabled":
      return (
        filter.type === "boolean" &&
        (filter.values.length === 0 || trigger.enabled === filter.values[0])
      );
    default:
      // Keep this switch in step with TRIGGER_FILTER_DEFINITIONS: an unhandled
      // field would silently match every row.
      return true;
  }
}

/** Whether ``trigger`` fires the way the quick toggle asks for. */
export function triggerFiresOn(
  trigger: TriggerV2Record,
  firesOn: FiresOnFilter,
): boolean {
  switch (firesOn) {
    case "all":
      return true;
    case "schedule":
      return isSchedule(trigger.fires_on);
    case "events":
      return !isSchedule(trigger.fires_on);
  }
}

/**
 * In-memory evaluation of the filter widget's rows against one trigger. The
 * list is fetched in one shot, so the server-side `filterToCondition` path
 * does not apply; this is its equivalent, with `matchMode` joining rows the
 * same way (AND requires every row, OR at least one).
 */
export function triggerMatchesFilters(
  trigger: TriggerV2Record,
  filters: readonly Filter[],
  matchMode: FilterMatchMode,
): boolean {
  if (filters.length === 0) {
    return true;
  }
  return matchMode === "OR"
    ? filters.some((filter) => matchesFilter(trigger, filter))
    : filters.every((filter) => matchesFilter(trigger, filter));
}
